import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaImage, FaArrowLeft, FaShoppingCart, FaTruck, FaMoneyBillWave } from 'react-icons/fa';
import { toast } from 'sonner';
import { getProduct } from '@/api/products';
import { useCart } from '@/context/CartContext';
import { formatPrice } from '@/lib/format';
import { ErrorState, LoadingState } from '@/modules/shop/components/ShopStates';
import Seo from '@/components/seo/Seo';
import { breadcrumbJsonLd, canonicalUrl, productJsonLd } from '@/config/seo';
import { absoluteMediaUrl, mediaUrl } from '@/lib/media';

/** Detalj proizvoda: galerija, izbor varijante, dodavanje u korpu. */
const ProductPage = () => {
  const { slug } = useParams();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState('loading');
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const load = useCallback(() => {
    let active = true;
    setStatus('loading');
    getProduct(slug)
      .then((data) => {
        if (!active) return;
        setProduct(data);
        setSelectedVariantId(data.variants?.[0]?.id ?? null);
        setActiveImage(0);
        setQuantity(1);
        setStatus('ready');
      })
      .catch((error) => {
        if (!active) return;
        setStatus(error?.response?.status === 404 ? 'missing' : 'error');
      });
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(load, [load]);

  const selectedVariant = useMemo(
    () => product?.variants?.find((variant) => variant.id === selectedVariantId) || null,
    [product, selectedVariantId],
  );

  /** Cena varijante ima prednost nad osnovnom cenom — isto pravilo kao na backendu. */
  const currentPrice = selectedVariant ? selectedVariant.price : product?.basePrice;

  const gallery = useMemo(() => {
    if (!product) return [];
    if (product.images?.length) return product.images;
    return product.imageUrl ? [{ url: product.imageUrl, alt: product.name, primary: true }] : [];
  }, [product]);

  const handleAddToCart = () => {
    addItem({
      productId: product.id ?? null,
      variantId: selectedVariant?.id ?? null,
      slug: product.slug,
      name: selectedVariant ? `${product.name} (${selectedVariant.name})` : product.name,
      price: Number(currentPrice),
      quantity,
      imageUrl: gallery[0]?.url || null,
    });
    toast.success('Proizvod je dodat u korpu.');
  };

  if (status === 'loading') {
    return (
      <div className="pt-32 min-h-[60vh]">
        <LoadingState label="Učitavanje proizvoda…" />
      </div>
    );
  }

  if (status === 'missing') {
    return (
      <div className="pt-32 min-h-[60vh] container-custom text-center py-24">
        <Seo title="Proizvod nije pronađen" noindex />
        <h1 className="font-heading text-3xl text-brand-primary mb-4">Proizvod nije pronađen</h1>
        <p className="text-neutral-500 mb-8">Moguće je da je uklonjen iz ponude.</p>
        <Link to="/shop" className="btn-accent inline-block">Nazad na prodavnicu</Link>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="pt-32 min-h-[60vh]">
        <ErrorState message="Proizvod trenutno nije moguće učitati." onRetry={load} />
      </div>
    );
  }

  const productPath = `/proizvod/${product.slug}`;
  // og:image i schema `image` moraju biti apsolutne URL — Fejsbuk/Viber i Google
  // ignorišu relativne putanje.
  const galleryUrls = gallery.map((image) => absoluteMediaUrl(image.url)).filter(Boolean);
  const productDescription =
    product.description || `${product.name} — ${formatPrice(currentPrice)}, isporuka širom Srbije, plaćanje pouzećem.`;

  return (
    <>
      <Seo
        title={product.name}
        description={productDescription}
        path={productPath}
        type="product"
        image={galleryUrls[0]}
        imageAlt={product.name}
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Prodavnica', path: '/shop' },
            ...(product.categoryName
              ? [{ name: product.categoryName, path: `/shop/${product.categorySlug}` }]
              : []),
            { name: product.name, path: productPath },
          ]),
          productJsonLd(product, {
            url: canonicalUrl(productPath),
            images: galleryUrls,
            price: currentPrice,
          }),
        ]}
      />

      <div className="pt-32 pb-20" data-testid="product-page">
        <div className="container-custom">
          <Link
            to={`/shop/${product.categorySlug}`}
            className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-brand-accent transition-colors mb-8"
          >
            <FaArrowLeft size={12} />
            Nazad na kategoriju
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Galerija */}
            <div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="aspect-square bg-neutral-50 overflow-hidden mb-4"
              >
                {gallery.length > 0 ? (
                  <img
                    src={mediaUrl(gallery[activeImage]?.url)}
                    alt={gallery[activeImage]?.alt || product.name}
                    className="w-full h-full object-cover"
                    data-testid="product-main-image"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-200">
                    <FaImage size={64} />
                  </div>
                )}
              </motion.div>

              {gallery.length > 1 && (
                <div className="grid grid-cols-5 gap-3" data-testid="product-thumbnails">
                  {gallery.map((image, index) => (
                    <button
                      key={image.url}
                      type="button"
                      onClick={() => setActiveImage(index)}
                      className={`aspect-square overflow-hidden border-2 transition-colors ${
                        index === activeImage ? 'border-brand-accent' : 'border-transparent hover:border-neutral-200'
                      }`}
                      aria-label={`Slika ${index + 1}`}
                    >
                      <img
                        src={mediaUrl(image.url)}
                        alt={image.alt || `${product.name} ${index + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Podaci i kupovina */}
            <div>
              <h1 className="font-heading text-3xl md:text-4xl text-brand-primary mb-4">
                {product.name}
              </h1>
              <p className="text-3xl text-brand-accent font-medium mb-8" data-testid="product-price">
                {formatPrice(currentPrice)}
              </p>

              {product.description && (
                <p className="text-neutral-700 leading-relaxed mb-8 whitespace-pre-line">
                  {product.description}
                </p>
              )}

              {product.variants?.length > 0 && (
                <div className="mb-8">
                  <span className="block text-sm font-medium text-brand-primary mb-3">
                    Izaberite varijantu
                  </span>
                  <div className="flex flex-wrap gap-3">
                    {product.variants.map((variant) => (
                      <button
                        key={variant.id}
                        type="button"
                        onClick={() => setSelectedVariantId(variant.id)}
                        className={`px-4 py-3 text-sm border rounded-sm transition-colors ${
                          variant.id === selectedVariantId
                            ? 'border-brand-accent bg-brand-bgSoft text-brand-primary'
                            : 'border-neutral-200 text-neutral-700 hover:border-brand-accent'
                        }`}
                        data-testid={`variant-${variant.id}`}
                      >
                        <span className="block">{variant.name}</span>
                        <span className="block text-xs text-neutral-500 mt-1">
                          {formatPrice(variant.price)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 mb-8">
                <span className="text-sm font-medium text-brand-primary">Količina</span>
                <div className="flex items-center border border-neutral-200 rounded-sm">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="px-4 py-2 text-brand-primary hover:text-brand-accent transition-colors"
                    aria-label="Smanji količinu"
                  >
                    −
                  </button>
                  <span className="px-4 py-2 min-w-[3rem] text-center" data-testid="product-quantity">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(999, q + 1))}
                    className="px-4 py-2 text-brand-primary hover:text-brand-accent transition-colors"
                    aria-label="Povećaj količinu"
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAddToCart}
                className="btn-accent w-full flex items-center justify-center gap-3 mb-6"
                data-testid="add-to-cart"
              >
                <FaShoppingCart size={16} />
                Dodaj u korpu
              </button>

              <div className="space-y-3 text-sm text-neutral-500 border-t border-neutral-200 pt-6">
                <p className="flex items-center gap-3">
                  <FaMoneyBillWave className="text-brand-accent" />
                  Plaćanje pouzećem — kuriru pri preuzimanju
                </p>
                <p className="flex items-center gap-3">
                  <FaTruck className="text-brand-accent" />
                  Dostava na teritoriji cele Srbije
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductPage;
